import { Logo } from "components";

type LegalTextBlock = { type: "h2" | "h3" | "p"; text: string };
type LegalBlock = LegalTextBlock | { type: "list"; items: string[] };

const lastUpdated = "July 28, 2026";
const supportEmail = "gataastudio@gmail.com";

const blocks: LegalBlock[] = [
  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "Welcome to LinksRoom, a platform developed by Gataa Studio for organizing, sharing, discovering, and managing links, files, profiles, rooms, stores, posts, articles, messages, and related content.",
  },
  {
    type: "p",
    text: "These Terms of Use govern your access to and use of LinksRoom, including our mobile applications, website, public profile pages, rooms, communication tools, analytics, paid features, and related services.",
  },
  {
    type: "p",
    text: "By accessing or using LinksRoom, you agree to these Terms. If you do not agree, you should not use LinksRoom.",
  },
  { type: "h2", text: "2. Eligibility" },
  {
    type: "p",
    text: "You must be at least 18 years old, or the age of legal majority in your jurisdiction, to use LinksRoom. By using LinksRoom, you confirm that you are legally able to enter into these Terms.",
  },
  {
    type: "p",
    text: "You may not use LinksRoom if you are prohibited from using the service under applicable laws, sanctions, platform rules, or these Terms.",
  },
  { type: "h2", text: "3. Accounts and Security" },
  {
    type: "p",
    text: "Some features require an account. You agree to provide accurate and current information, including your name, email address, profile information, and any other information required for account creation or profile setup.",
  },
  {
    type: "p",
    text: `You are responsible for keeping your login credentials secure and for all activity that occurs under your account. If you believe your account has been accessed without permission, contact us as soon as possible at ${supportEmail}.`,
  },
  {
    type: "p",
    text: "We may require account verification, email verification, or other security steps before allowing access to certain features.",
  },
  { type: "h2", text: "4. Use of LinksRoom" },
  { type: "h3", text: "4.1 Permitted use" },
  {
    type: "p",
    text: "You may use LinksRoom for personal, professional, creator, community, or business purposes, as long as your use complies with these Terms, our policies, and applicable laws.",
  },
  { type: "h3", text: "4.2 Profiles, links, files, and content" },
  {
    type: "p",
    text: "LinksRoom lets you create a digital profile and organize links, files, images, audio files, posts, articles, products, banners, bookmarks, rooms, and other content. You are responsible for all content you create, upload, import, publish, reshare, send, or make available through LinksRoom.",
  },
  { type: "h3", text: "4.3 Visibility and sharing controls" },
  {
    type: "p",
    text: "LinksRoom may allow you to make content public, private, or visible only to selected users, contacts, approved people, followers, rooms, or groups. You are responsible for choosing the correct visibility settings before sharing content.",
  },
  {
    type: "p",
    text: "Public content, public profiles, public rooms, public room links, QR links, reshared content, and content shared outside the app may be viewed by other people and may be accessible without a LinksRoom account.",
  },
  { type: "h3", text: "4.4 Rooms" },
  {
    type: "p",
    text: "Rooms allow users to organize and share links, files, posts, articles, products, members, and related content around a topic, group, or purpose. Room owners and administrators are responsible for managing room information, members, requests, invitations, visibility, and content shared inside their rooms.",
  },
  {
    type: "p",
    text: "Rooms may include an About section, public links, invitations, join requests, QR codes, and shared content. You must not use rooms to mislead users, impersonate others, distribute prohibited content, or violate anyone's rights.",
  },
  { type: "h3", text: "4.5 Bookmark import" },
  {
    type: "p",
    text: "LinksRoom may allow you to import browser bookmark files or other supported files to add multiple links. You are responsible for making sure that imported links are lawful, safe, accurate, and permitted to be stored or shared by you.",
  },
  { type: "h3", text: "4.6 Resharing and engagement" },
  {
    type: "p",
    text: "LinksRoom may allow users to like, save, share, reshare, comment on, or otherwise interact with content. Resharing content does not transfer ownership of that content to you. You must not reshare content in a misleading, abusive, unlawful, or rights-infringing way.",
  },
  { type: "h3", text: "4.7 Messaging and communication" },
  {
    type: "p",
    text: "LinksRoom may include direct messages, in-app chat, replies, reactions, invitations, requests, notifications, and other communication features. You are responsible for messages and content you send. You must not use messaging features for spam, harassment, scams, threats, unlawful activity, or unwanted commercial messages.",
  },
  { type: "h3", text: "4.8 Local Communication and location-based features" },
  {
    type: "p",
    text: "LinksRoom may offer location-based features, such as discovering nearby profiles or people within an approximate distance. You are responsible for how you use these features and for respecting the privacy, safety, and choices of other users. Do not use location-based features to stalk, harass, track, threaten, or harm anyone.",
  },
  { type: "h3", text: "4.9 Stores, products, and orders" },
  {
    type: "p",
    text: "LinksRoom may allow users to create stores, list products, manage categories, receive orders, and communicate with buyers or sellers. Sellers are responsible for the accuracy, legality, availability, pricing, taxes, delivery, support, and fulfillment of their products and orders. Buyers and sellers are responsible for complying with applicable consumer, tax, payment, and commerce laws.",
  },
  {
    type: "p",
    text: "LinksRoom is not responsible for products, services, transactions, delivery, refunds, disputes, or claims between users unless required by applicable law.",
  },
  { type: "h3", text: "4.10 Free and paid features" },
  {
    type: "p",
    text: "LinksRoom may offer free features and subscription-based or paid features. Free accounts may have limits, including limits on links, files, storage, upload size, rooms, analytics, profile customization, stores, posts, articles, banners, promotions, or other features. Paid plans may provide additional capacity and functionality.",
  },
  { type: "h2", text: "5. Prohibited Conduct" },
  { type: "p", text: "You agree not to:" },
  {
    type: "list",
    items: [
      "Use LinksRoom for any unlawful, harmful, fraudulent, deceptive, abusive, or unauthorized purpose.",
      "Share links, files, messages, images, audio, posts, articles, products, rooms, or other content that is illegal, abusive, harassing, hateful, discriminatory, sexually explicit, pornographic, exploitative, violent, misleading, defamatory, or otherwise harmful.",
      "Upload, publish, reshare, or distribute child sexual abuse material, non-consensual intimate content, sexual exploitation content, terrorist content, extremist content, or content that promotes self-harm, violence, or dangerous activities.",
      "Share spam, malware, phishing links, scams, illegal products, illegal drugs, counterfeit goods, weapons, or content that facilitates unlawful activity.",
      "Upload or share another person's private links, files, photos, messages, personal information, confidential material, or account information without permission.",
      "Impersonate another person, brand, company, organization, public figure, or government entity, or misrepresent your affiliation with them.",
      "Use LinksRoom to stalk, harass, threaten, intimidate, dox, exploit, or harm another person.",
      "Abuse Local Communication, room discovery, profile discovery, messaging, requests, invitations, comments, reactions, or notifications.",
      "Circumvent blocks, restrictions, suspensions, visibility controls, privacy settings, access controls, or safety features.",
      "Interfere with the security, availability, integrity, or proper operation of LinksRoom.",
      "Attempt to access systems, accounts, content, or data without authorization.",
      "Collect, scrape, crawl, index, copy, or harvest personal information or content from LinksRoom without permission.",
      "Use bots, automated tools, scripts, or other means to spam, manipulate engagement, inflate analytics, or abuse the service.",
      "Violate intellectual property, privacy, publicity, consumer protection, payment, export, sanctions, or other applicable laws.",
    ],
  },
  { type: "h2", text: "6. Content Moderation, Image Review, Reporting, and Blocking" },
  {
    type: "p",
    text: "LinksRoom may provide reporting, blocking, privacy, and content moderation tools. Users may report content, profiles, rooms, products, messages, or behavior that appears to violate these Terms.",
  },
  {
    type: "p",
    text: "We may review uploaded or shared content, including images and visual content, using automated systems, manual review, service providers, or a combination of these methods. This review may be used to detect potentially unsafe, illegal, abusive, sexually explicit, violent, fraudulent, infringing, or otherwise prohibited content.",
  },
  {
    type: "p",
    text: "We may remove content, limit visibility, disable sharing, restrict uploads, reject content, issue warnings, suspend accounts, terminate accounts, or take other action when we believe it is appropriate.",
  },
  {
    type: "p",
    text: "Blocking another user may limit that user's ability to view, interact with, message, request, or access certain parts of your profile or content. Blocking is a safety and privacy tool, but it may not remove all previous interactions, records, notifications, messages, or content already shared with others.",
  },
  { type: "h2", text: "7. Your Content and License to LinksRoom" },
  {
    type: "p",
    text: "You keep ownership of the links, files, profile information, messages, images, audio, text, posts, articles, products, rooms, banners, and other content that you submit to LinksRoom.",
  },
  {
    type: "p",
    text: "You are responsible for making sure you have all rights, licenses, permissions, and legal authority needed to upload, import, publish, display, send, reshare, or otherwise use that content through LinksRoom.",
  },
  {
    type: "p",
    text: "By submitting content to LinksRoom, you give Gataa Studio a limited, worldwide, non-exclusive, royalty-free license to host, store, process, reproduce, display, transmit, translate, moderate, analyze, and make your content available only as needed to operate, maintain, protect, improve, and provide LinksRoom and its features.",
  },
  {
    type: "p",
    text: "This license ends when your content is deleted from LinksRoom, except where retention is required or permitted for backups, account recovery, security, fraud prevention, dispute resolution, enforcement, legal compliance, moderation records, or legitimate operational needs.",
  },
  { type: "h2", text: "8. Privacy" },
  {
    type: "p",
    text: "Our Privacy Policy explains how we collect, use, disclose, store, and protect information when you use LinksRoom. By using LinksRoom, you also acknowledge the practices described in our Privacy Policy.",
  },
  { type: "h2", text: "9. Subscriptions, Purchases, and Cancellation" },
  {
    type: "p",
    text: "LinksRoom may offer monthly, yearly, or other subscription plans and paid features. Subscription terms, pricing, renewal rules, trial terms, and cancellation options will be shown at the time of purchase or through the relevant app store or payment provider.",
  },
  {
    type: "p",
    text: "If you purchase through Apple App Store, Google Play, or another third-party provider, your purchase is also subject to that provider's terms, payment rules, refund rules, and account settings.",
  },
  {
    type: "p",
    text: "You can cancel a subscription according to the process provided in the app, website, app store, or payment provider used for the purchase. Unless required by law or stated otherwise at the time of purchase, payments are generally non-refundable.",
  },
  { type: "h2", text: "10. Third-Party Services and Links" },
  {
    type: "p",
    text: "LinksRoom may allow you to sign in, connect, view, share, or pay using third-party services such as Google, Apple, Facebook, app stores, payment providers, analytics providers, hosting providers, or external websites.",
  },
  {
    type: "p",
    text: "Your use of third-party services is subject to their own terms and privacy policies. LinksRoom is not responsible for third-party services, websites, content, products, payments, security, availability, or actions.",
  },
  { type: "h2", text: "11. Service Changes and Availability" },
  {
    type: "p",
    text: "We may update, change, suspend, limit, or discontinue parts of LinksRoom from time to time. Features may vary by country, device, platform, app version, account type, subscription status, permissions, or operational requirements.",
  },
  {
    type: "p",
    text: "We do not guarantee that LinksRoom or any content, analytics, messages, rooms, public links, QR links, notifications, or features will always be available, accurate, uninterrupted, or error-free.",
  },
  { type: "h2", text: "12. Account Suspension and Termination" },
  {
    type: "p",
    text: "We may suspend, restrict, or terminate your account or access to LinksRoom if we believe that you have violated these Terms, created safety risks, abused the platform, violated applicable law, infringed rights, attempted to bypass restrictions, or created legal, security, or operational risk for LinksRoom, Gataa Studio, users, or others.",
  },
  {
    type: "p",
    text: `You may delete your account using the available account deletion tools or by contacting us at ${supportEmail}. Account deletion is handled according to our Privacy Policy and applicable law.`,
  },
  { type: "h2", text: "13. Changes to These Terms" },
  {
    type: "p",
    text: "We may update these Terms from time to time. If changes are significant, we may provide notice through the app, website, email, app store listing, or another reasonable method.",
  },
  {
    type: "p",
    text: "Your continued use of LinksRoom after updated Terms are posted means you accept the updated Terms.",
  },
  { type: "h2", text: "14. Geographic and Legal Restrictions" },
  {
    type: "p",
    text: "LinksRoom may not be available in all countries or regions. You are responsible for making sure your use of LinksRoom is allowed where you live or access the service. We may restrict access where required by law, platform rules, sanctions, technical limitations, safety concerns, or operational reasons.",
  },
  { type: "h2", text: "15. Disclaimer of Warranties" },
  {
    type: "p",
    text: 'LinksRoom is provided "as is" and "as available." To the fullest extent permitted by law, Gataa Studio makes no warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, availability, accuracy, reliability, security, or uninterrupted operation.',
  },
  { type: "h2", text: "16. Limitation of Liability" },
  {
    type: "p",
    text: "To the fullest extent permitted by applicable law, Gataa Studio will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost data, loss of goodwill, service interruption, content loss, account restriction, unauthorized access, user transactions, third-party conduct, or user-generated content arising from or related to your use of LinksRoom.",
  },
  { type: "h2", text: "17. Governing Law" },
  {
    type: "p",
    text: "These Terms will be governed by the applicable laws of the jurisdiction in which Gataa Studio operates, unless otherwise required by mandatory consumer protection laws.",
  },
  { type: "h2", text: "18. Contact Us" },
  {
    type: "p",
    text: `If you have questions about these Terms or LinksRoom, contact Gataa Studio at ${supportEmail}.`,
  },
];

const createLegalId = (text: string, index: number) =>
  `${index}-${text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;

const getHeadingId = (block: LegalTextBlock, index: number) =>
  createLegalId(block.text, index);

const tableOfContents = blocks
  .map((block, index) => {
    if (block.type !== "h2" && block.type !== "h3") {
      return null;
    }

    return {
      id: getHeadingId(block, index),
      level: block.type,
      text: block.text,
    };
  })
  .filter(
    (item): item is { id: string; level: "h2" | "h3"; text: string } =>
      Boolean(item)
  );

export default function Terms() {
  return (
    <>
      <style>
        {`
body {
  margin: 0;
  background-color: #f4f4f4;
}

.legal-page {
  min-height: 100vh;
  padding: 24px 16px;
  font-family: Arial, sans-serif;
  color: #333;
}

.legal-container {
  max-width: 840px;
  margin: 0 auto;
  padding: 28px;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.legal-header {
  text-align: center;
  padding-bottom: 24px;
  border-bottom: 1px solid #ddd;
}

.legal-logo {
  width: 150px;
  margin: 0 auto 12px;
}

.legal-title {
  margin: 0;
  font-size: 26px;
  line-height: 1.25;
  color: #222;
}

.legal-subtitle {
  margin: 8px 0 0;
  font-size: 15px;
  color: #666;
}

.legal-main {
  padding: 24px 0 0;
}

.legal-toc {
  margin: 24px 0 0;
  padding: 18px;
  background-color: #f8fafc;
  border: 1px solid #e5e7eb;
}

.legal-toc-title {
  margin: 0 0 12px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
  color: #222;
}

.legal-toc-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.legal-toc-item {
  margin-left: 0;
}

.legal-toc-item-h3 {
  margin-left: 18px;
}

.legal-toc-link,
.legal-heading-link {
  color: inherit;
  text-decoration: none;
}

.legal-toc-link {
  display: inline-block;
  font-size: 15px;
  line-height: 1.45;
  color: #2563eb;
}

.legal-toc-link:hover,
.legal-heading-link:hover {
  text-decoration: underline;
}

.legal-main h2,
.legal-main h3 {
  scroll-margin-top: 20px;
}

.legal-main h2 {
  margin: 28px 0 10px;
  font-size: 20px;
  line-height: 1.35;
  color: #222;
}

.legal-main h2:first-child {
  margin-top: 0;
}

.legal-main h3 {
  margin: 18px 0 8px;
  font-size: 17px;
  line-height: 1.35;
  color: #333;
}

.legal-main p,
.legal-main li {
  font-size: 16px;
  line-height: 1.65;
  color: #5f5f5f;
}

.legal-main p {
  margin: 0 0 14px;
}

.legal-main ul {
  margin: 0 0 14px;
  padding-left: 22px;
}

@media (max-width: 600px) {
  .legal-page {
    padding: 12px;
  }

  .legal-container {
    padding: 18px;
  }

  .legal-title {
    font-size: 23px;
  }
}
`}
      </style>

      <div className="legal-page">
        <div className="legal-container">
          <header className="legal-header">
            <Logo variant="logo-text-row-dark" className="legal-logo" />
            <h1 className="legal-title">LinksRoom Terms of Use</h1>
            <p className="legal-subtitle">Last updated: {lastUpdated}</p>
          </header>

          <nav className="legal-toc" aria-label="Terms sections">
            <p className="legal-toc-title">Contents</p>
            <ul className="legal-toc-list">
              {tableOfContents.map((item) => (
                <li
                  key={item.id}
                  className={`legal-toc-item legal-toc-item-${item.level}`}
                >
                  <a className="legal-toc-link" href={`#${item.id}`}>
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <main className="legal-main">
            {blocks.map((block, index) => {
              if (block.type === "list") {
                return (
                  <ul key={index}>
                    {block.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              }

              if (block.type === "p") {
                return <p key={index}>{block.text}</p>;
              }

              const id = getHeadingId(block, index);
              const Component = block.type;
              return (
                <Component id={id} key={index}>
                  <a className="legal-heading-link" href={`#${id}`}>
                    {block.text}
                  </a>
                </Component>
              );
            })}
          </main>
        </div>
      </div>
    </>
  );
}
